// src/pages/Folder/PerformanceDetail.export.ts

import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'

import {
  MMMMYYYY,
  removeUnits,
  sensorType,
  sensorUnit,
  userLocales,
  WDDDMMYYYY,
  WDDDMMYYYYHHMM,
} from '../../api/'

const borderStyle = {
  top: { style: 'medium', color: { argb: 'FF000000' } },
  left: { style: 'medium', color: { argb: 'FF000000' } },
  bottom: { style: 'medium', color: { argb: 'FF000000' } },
  right: { style: 'medium', color: { argb: 'FF000000' } },
}

const fillStyle = {
  fgColor: { argb: 'FFC0C0C0' },
  pattern: 'solid',
  type: 'pattern',
}

const parseNumber = data =>
  typeof data === 'string'
    ? Number(data.replace('.', '').replace(',', '.'))
    : data

const handleNaN = data => (isNaN(data) ? String('-') : data)

const configureColumns = cols => {
  const data = [{}]

  if (cols.date === true) data.push({ width: 20 })
  if (cols.actual_consumption === true) data.push({ width: 20 })
  if (cols.trend === true) data.push({ width: 20 })
  if (cols.counter_reading === true) data.push({ width: 20 })
  if (cols.emissions === true) data.push({ width: 20 })
  if (cols.area_consumption === true) data.push({ width: 30 })
  if (cols.employee_consumption === true) data.push({ width: 30 })
  if (cols.actual_costs === true) data.push({ width: 20 })
  if (cols.area_costs === true) data.push({ width: 20 })
  if (cols.employee_costs === true) data.push({ width: 20 })
  if (cols.benchmark === true) data.push({ width: 20 })

  const minAmountOfColumns = 11

  if (data.length < minAmountOfColumns) {
    for (let i = data.length; i < minAmountOfColumns; i++)
      data.push({ width: 20 })
  }

  return data
}

const generateCaptions = (cols, unit, uch) => {
  const data = ['', 'Datum']

  const consumptionUnit = uch ? ` (${unit})` : ''
  const currency = uch ? ' (€)' : ''
  const emissionsUnit = uch ? ' (kg)' : ''

  if (cols.actual_consumption === true)
    data.push(`IST Verbrauch${consumptionUnit}`)
  if (cols.trend === true) data.push('Trend (%)')
  if (cols.counter_reading === true) data.push('Zählerstand')
  if (cols.emissions === true) data.push(`CO₂-Emissionen${emissionsUnit}`)
  if (cols.area_consumption === true)
    data.push(`Verbrauch / m²${consumptionUnit})`)
  if (cols.employee_consumption === true)
    data.push(`Verbrauch / Mitarbeiter${consumptionUnit}`)
  if (cols.actual_costs === true) data.push(`IST Kosten${currency}`)
  if (cols.area_costs === true) data.push(`Kosten / m²${currency}`)
  if (cols.employee_costs === true) data.push(`Kosten / Mitarbeiter${currency}`)
  if (cols.benchmark === true) data.push(`Benchmark (%)`)

  return data
}

const generateRow = (cols, data, granularity, ucv) => {
  const record = ['']

  if (cols.date === true) {
    record.push(
      typeof data[0] === 'object'
        ? new Date(data[0]).toLocaleString(
            userLocales,
            granularity === 'd'
              ? WDDDMMYYYYHHMM
              : granularity === 'y'
                ? MMMMYYYY
                : WDDDMMYYYY
          )
        : 'Summe'
    )
  }

  if (cols.actual_consumption === true)
    record.push(handleNaN(parseNumber(removeUnits(data[1]))))

  if (cols.trend === true)
    record.push(ucv === true ? data[2] : removeUnits(data[2]))

  if (cols.counter_reading === true) record.push(data[9])

  if (cols.emissions === true)
    record.push(handleNaN(parseNumber(removeUnits(data[3]))))

  if (cols.area_consumption === true)
    record.push(handleNaN(parseNumber(removeUnits(data[4]))))

  if (cols.employee_consumption === true)
    record.push(handleNaN(parseNumber(removeUnits(data[5]))))

  if (cols.actual_costs === true)
    record.push(handleNaN(parseNumber(removeUnits(data[6]))))

  if (cols.area_costs === true)
    record.push(handleNaN(parseNumber(removeUnits(data[7]))))

  if (cols.employee_costs === true)
    record.push(handleNaN(parseNumber(removeUnits(data[10]))))

  if (cols.benchmark === true) record.push(data[8])

  return record
}

const generateSummary = cols => {
  const data = ['', 'Summe']

  if (cols.actual_consumption === true) data.push('')
  if (cols.trend === true) data.push('')
  if (cols.counter_reading === true) data.push('')
  if (cols.emissions === true) data.push('')
  if (cols.area_consumption === true) data.push('')
  if (cols.employee_consumption === true) data.push('')
  if (cols.actual_costs === true) data.push('')
  if (cols.area_costs === true) data.push('')
  if (cols.employee_costs === true) data.push('')
  if (cols.benchmark === true) data.push('')

  return data
}

const getLetter = num => String.fromCharCode(64 + num)

// catches error with sensor with same names
// exceljs demands unique sheet names
const EXCEL_SHEET_MAX = 31

const sanitizeSheetName = s => {
  let cleaned = String(s ?? 'Sheet')
    .replaceAll(/[\\/?*[\]:]/g, '–')
    .replaceAll(/\s+/g, ' ')
    .trim()

  if (!cleaned) cleaned = 'Sheet'
  return cleaned
}

const uniqueSheetName = (workbook, base) => {
  const cleanedBase = sanitizeSheetName(base)

  const first = cleanedBase.slice(0, EXCEL_SHEET_MAX)
  if (!workbook.getWorksheet(first)) return first

  for (let i = 2; i < 10000; i++) {
    const suffix = ` (${i})`
    const maxBaseLen = EXCEL_SHEET_MAX - suffix.length
    const candidate =
      sanitizeSheetName(cleanedBase).slice(0, Math.max(1, maxBaseLen)) + suffix

    const finalName = candidate.slice(0, EXCEL_SHEET_MAX)
    if (!workbook.getWorksheet(finalName)) return finalName
  }

  return `Sheet-${Date.now()}`.slice(0, EXCEL_SHEET_MAX)
}

export const exportToExcel = async (
  data,
  filename,
  prices,
  folderData,
  roomsMode,
  medium,
  granularity,
  viewCols,
  unitConfig
) => {
  const unit = sensorUnit(medium)

  const { header: uch, values: ucv } = unitConfig

  const workbook = new ExcelJS.Workbook()

  const worksheet = []

  const refCols = () => [
    '',
    '',
    ...[
      'actual_consumption',
      'trend',
      'counter_reading',
      'emissions',
      'area_consumption',
      'employee_consumption',
      'actual_costs',
      'area_costs',
      'employee_costs',
      'benchmark',
    ].filter(key => viewCols[key] === true),
  ]

  const FMT_CONSUMPTION = '#,##0.00 "'
  const FMT_COSTS = '#,##0.00 "€"'

  if (Object.keys(data).length > 0) {
    Object.keys(data).map(name => {
      const dataCount = data[name].length

      worksheet[name] = workbook.addWorksheet(uniqueSheetName(workbook, name))

      const setupCell = (id, value, border = borderStyle, fill = fillStyle) => {
        worksheet[name].getCell(id).border = border
        worksheet[name].getCell(id).fill = fill
        worksheet[name].getCell(id).value = value
      }

      worksheet[name].columns = configureColumns(viewCols)

      worksheet[name].addRow([])

      worksheet[name].addRow(generateCaptions(viewCols, unit, uch))

      const _ref = refCols()
      const trendKeyIndex = _ref.findIndex(k => k === 'trend') + 1
      const benchmarkKeyIndex = _ref.findIndex(k => k === 'benchmark') + 1

      if (ucv === true) {
        const colIndexByKey = k => _ref.findIndex(x => x === k) + 1
        const setFmt = (k, fmt) => {
          const idx = colIndexByKey(k)
          if (idx > 0) worksheet[name].getColumn(idx).numFmt = fmt
        }
        setFmt('actual_consumption', FMT_CONSUMPTION + unit + '"')
        setFmt('area_consumption', FMT_CONSUMPTION + unit + '"')
        setFmt('employee_consumption', FMT_CONSUMPTION + unit + '"')
        setFmt('emissions', '#,##0.00 "kg"')
        setFmt('actual_costs', FMT_COSTS)
        setFmt('area_costs', FMT_COSTS)
        setFmt('employee_costs', FMT_COSTS)
      }

      for (let i = 0; i < dataCount; i++) {
        const isUpBenchmark = String(data[name][i][8]).includes('▲')
        const isDownBenchmark = String(data[name][i][8]).includes('▼')
        const isUpTrend = String(removeUnits(data[name][i][2])).includes('▲')
        const isDownTrend = String(removeUnits(data[name][i][2])).includes('▼')

        if (i === dataCount - 1) {
          const row = worksheet[name].addRow(generateSummary(viewCols))

          row.eachCell((cell, key) => {
            const avgBenchmark =
              data[name]
                .map(item =>
                  parseFloat(
                    String(item[8]).replace('▲ ', '').replace('▼ ', '')
                  )
                )
                .reduce((acc, val) => acc + val, 0) / dataCount

            const ln = dataCount + 1
            const col = getLetter(key)

            if (_ref[key] === 'actual_consumption') {
              cell.value = {
                formula: `SUM(${col}3:${col}${ln})`,
                result: undefined,
              }
            }

            if (_ref[key] === 'emissions') {
              cell.value = {
                formula: `SUM(${col}3:${col}${ln})`,
                result: undefined,
              }
            }

            if (_ref[key] === 'area_consumption') {
              cell.value = {
                formula: `SUM(${col}3:${col}${ln})`,
                result: undefined,
              }
            }

            if (_ref[key] === 'employee_consumption') {
              cell.value = {
                formula: `SUM(${col}3:${col}${ln})`,
                result: undefined,
              }
            }

            if (_ref[key] === 'actual_costs') {
              cell.value = {
                formula: `SUM(${col}3:${col}${ln})`,
                result: undefined,
              }
            }

            if (_ref[key] === 'area_costs') {
              cell.value = {
                formula: `SUM(${col}3:${col}${ln})`,
                result: undefined,
              }
            }

            if (_ref[key] === 'benchmark') {
              cell.font = {
                color: {
                  argb: isUpBenchmark
                    ? 'FFAA0000'
                    : isDownBenchmark
                      ? 'FF00AA00'
                      : 'FF000000',
                },
              }
              cell.value =
                typeof avgBenchmark === 'number' && avgBenchmark > 0
                  ? `▲ ${Number(avgBenchmark).toFixed(2)}`
                  : typeof avgBenchmark === 'number' && avgBenchmark < 0
                    ? `▼ ${Number(avgBenchmark).toFixed(2)}`
                    : !Number.isFinite(avgBenchmark)
                      ? '-'
                      : `${Number(avgBenchmark).toFixed(2)}`
            }

            if (_ref[key] === 'employee_costs') {
              cell.value = {
                formula: `SUM(${col}3:${col}${ln})`,
                result: undefined,
              }
            }
          })

          row.getCell(2).value = 'Summe'

          const actualIdx = _ref.findIndex(k => k === 'actual_consumption') + 1
          if (actualIdx > 0) {
            const actualCol = getLetter(actualIdx)
            const ln = dataCount + 1
            const sumCell = row.getCell(actualIdx)
            if (sumCell.value === null || sumCell.value === '') {
              sumCell.value = {
                formula: `SUM(${actualCol}3:${actualCol}${ln})`,
                result: undefined,
              }
            }
          }

          const counterIdx = _ref.findIndex(k => k === 'counter_reading') + 1
          if (counterIdx > 0) row.getCell(counterIdx).value = ''
        } else {
          const row = worksheet[name].addRow(
            generateRow(viewCols, data[name][i], granularity, ucv)
          )

          row.eachCell((cell, key) => {
            if (key > 1) {
              cell.border = {
                left: { style: 'medium', color: { argb: 'FF000000' } },
                right: { style: 'medium', color: { argb: 'FF000000' } },
              }
            }

            if (key === benchmarkKeyIndex) {
              cell.font = {
                color: {
                  argb: isUpBenchmark
                    ? 'FFAA0000'
                    : isDownBenchmark
                      ? 'FF00AA00'
                      : 'FF000000',
                },
              }
            }

            if (key === trendKeyIndex) {
              cell.font = {
                color: {
                  argb: isUpTrend
                    ? 'FFAA0000'
                    : isDownTrend
                      ? 'FF00AA00'
                      : 'FF000000',
                },
              }
            }
          })
        }
      }

      for (let i = 0; i < 6; i++)
        worksheet[name].addRow(['', '', '', '', '', '', '', '', '', '', '', ''])

      worksheet[name].getRow(2).eachCell((cell, key) => {
        if (key > 1) {
          cell.border = borderStyle
          cell.fill = fillStyle
        }
      })

      worksheet[name].getRow(2 + dataCount).eachCell((cell, key) => {
        if (key > 1) {
          cell.border = borderStyle
          cell.fill = fillStyle
        }
      })

      if (name === 'Gesamtverbrauch') {
        const row = dataCount + 4
        const consumptionUnit = uch ? ` (${unit})` : ''

        setupCell(`B${row}`, 'Medium')
        setupCell(`C${row}`, 'Zähler')

        setupCell(`E${row}`, `Verbrauch${consumptionUnit}`)
        setupCell(`F${row}`, `Minimum${consumptionUnit}`)
        setupCell(`G${row}`, `Maximum${consumptionUnit}`)
        setupCell(`H${row}`, `Durchschnitt${consumptionUnit}`)

        setupCell(`B${row + 1}`, sensorType(medium), borderStyle, null)
        setupCell(`C${row + 1}`, name, borderStyle, null)
        setupCell(
          `E${row + 1}`,
          { formula: `=C${dataCount + 2}`, result: undefined },
          borderStyle,
          null
        )
        worksheet[name].getCell(`E${row + 1}`).numFmt =
          '#,##0.00 "' + unit + '"'
        setupCell(
          `F${row + 1}`,
          { formula: `MIN(C3:C${dataCount + 1})`, result: undefined },
          borderStyle,
          null
        )
        setupCell(
          `G${row + 1}`,
          { formula: `MAX(C3:C${dataCount + 1})`, result: undefined },
          borderStyle,
          null
        )
        setupCell(
          `H${row + 1}`,
          {
            formula: `ROUND(AVERAGE(C3:C${dataCount + 1}), 2)`,
            result: undefined,
          },
          borderStyle,
          null
        )

        setupCell(`B${row + 3}`, 'Stammdaten')
        setupCell(`C${row + 3}`, '')

        setupCell(`B${row + 4}`, roomsMode, borderStyle, null)

        setupCell(
          `C${row + 4}`,
          folderData?.number_of_employees || '-',
          borderStyle,
          null
        )
        worksheet[name].getCell(`C${row + 4}`).numFmt = 'General'
      }

      if (name !== 'Gesamtverbrauch') {
        const sensorByName = name =>
          folderData?.sensors?.find(f => f.name === name)
        const pSensorId = sensorByName(name).entity_id
        const pRecord = prices.find(f => f.sensor_id === pSensorId)
        const row = dataCount + 4
        const currency = uch ? ' (€)' : ''

        setupCell(`B${row}`, `${sensorType(medium)}kosten / ${unit}${currency}`)

        setupCell(
          `C${row}`,
          pRecord?.price
            ? parseNumber(pRecord?.price.toLocaleString(userLocales))
            : '-',
          borderStyle,
          null
        )

        setupCell(`B${row + 1}`, `Fixkosten / Jahr${currency}`)

        setupCell(
          `C${row + 1}`,
          pRecord?.price_for_year
            ? parseNumber(pRecord?.price_for_year.toLocaleString(userLocales))
            : '-',
          borderStyle,
          null
        )
      }

      worksheet[name].eachRow(row => {
        row.eachCell({ includeEmpty: true }, cell => {
          cell.alignment = { horizontal: 'center', vertical: 'middle' }
        })
      })

      return null
    })
  }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  saveAs(blob, filename)
}
